import React, { useEffect, useState } from 'react';
import TimePicker from '../TimePicker/TimePicker';
import style from './TimeModal.module.css';

interface TimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (startAt: string, finishAt: string) => void;
  position: { top: number; left: number };
  initialStartAt?: string;
  initialFinishAt?: string;
}

const TimeModal: React.FC<TimeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  position,
  initialStartAt = '00:00',
  initialFinishAt = '00:00',
}) => {
  const [startAt, setStartAt] = useState(initialStartAt);
  const [finishAt, setFinishAt] = useState(initialFinishAt);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSave = () => {
    if (!isSaveDisabled) {
      onSave(startAt, finishAt);
      onClose();
      resetTime();
    }
  };

  const handleClose = () => {
    onClose();
    resetTime();
  };

  const resetTime = () => {
    setStartAt(initialStartAt);
    setFinishAt(initialFinishAt);
    setIsSaveDisabled(true);
    setErrorMessage(null);
  };

  useEffect(() => {
    if (startAt !== '00:00' && finishAt !== '00:00') {
      const [startHour, startMinute] = startAt.split(':').map(Number);
      const [finishHour, finishMinute] = finishAt.split(':').map(Number);

      if (
        finishHour < startHour ||
        (finishHour === startHour && finishMinute <= startMinute)
      ) {
        setIsSaveDisabled(true);
        setErrorMessage(
          'Час закінчення не може бути раніше або одночасно з часом початку',
        );
      } else {
        setIsSaveDisabled(false);
        setErrorMessage(null);
      }
    } else {
      setIsSaveDisabled(false);
    }
  }, [startAt, finishAt]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const modal = document.getElementById('modal');
      if (modal && !modal.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setStartAt(initialStartAt);
    setFinishAt(initialFinishAt);
  }, [initialStartAt, initialFinishAt]);

  if (!isOpen) return null;

  const modalStyle = {
    top: position.top,
    left: position.left,
    transform: 'translate(-50%, -50%)',
  };

  return (
    <div className={style.modalOverlay}>
      <div id="modal" className={style.modalContent} style={modalStyle}>
        <div className={style.timeChange}>
          <TimePicker label="Початок" value={startAt} onChange={setStartAt} />
          <TimePicker label="Кінець" value={finishAt} onChange={setFinishAt} />
        </div>
        {errorMessage && <p className={style.error}>{errorMessage}</p>}
        <div className={style.buttons}>
          <button
            onClick={handleSave}
            className={isSaveDisabled ? style.saveDisabled : style.saveButton}
            disabled={isSaveDisabled}
          >
            Зберегти
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeModal;
